import { useTranslation } from 'react-i18next';
/**
 * GitlabOnboardingModal — 3-krokový průvodce pro vložení GitLab PAT.
 *
 * Filip 2026-07-07: "zosobní ho v gitlabu a pomože mu to založit PAT a přenést".
 *
 * Krok 1: KC identity check (kdo jsem, GitLab mě přes SSO pozná)
 * Krok 2: "Otevřít GitLab" — nový tab s pre-fill URL (name=Codable, scopes=api,write_repository)
 * Krok 3: Paste PAT → volá gitlab.credentials_set → onSuccess pokračuje s project_start
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ExternalLink, CheckCircle2, User, KeyRound, Loader2 } from 'lucide-react';
import { api, ApiClientError } from '../../../lib/api';
import { Button } from '../../../components/ui';

interface CheckResp {
  ok: boolean;
  access_mode?: 'delegation';
  identity?: {
    tenant_id: string | null;
    entity_id: string | null;
    authenticated: boolean;
  };
  gitlab?: {
    host: string | null;
    has_credentials: boolean;
    source: 'tenant' | 'platform' | 'none' | 'delegation';
    error?: string | null;
  };
  onboarding_url?: string | null;
  hint?: string;
}

interface Props {
  onReady: () => void;   // credentials ready → dispatchni parent
  onCancel: () => void;
}

export default function GitlabOnboardingModal({
  onReady, onCancel }: Props) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'check' | 'open' | 'paste' | 'done'>('check');
  const [pat, setPat] = useState('');

  const check = useQuery({
    queryKey: ['codable-gitlab-check'],
    queryFn: () => api.mcp<CheckResp>('codable.gitlab_check', {}),
    staleTime: 0,
  });

  // Když má credentials → přeskočíme na done a upozorníme parent
  useEffect(() => {
    if (check.data?.gitlab?.has_credentials) {
      setStep('done');
      // krátký delay ať user vidí ✓
      const t = setTimeout(() => onReady(), 400);
      return () => clearTimeout(t);
    }
    if (check.data?.gitlab?.host && step === 'check') setStep('open');
  }, [check.data, step, onReady]);

  const save = useMutation({
    mutationFn: async () => {
      if (!pat.trim()) throw new Error(t('codable.glInsertPat'));
      return api.mcp<{ ok: boolean; error?: string }>('gitlab.credentials_set', {
        token: pat.trim(),
      });
    },
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('codable.glSaved'));
        setStep('done');
        setTimeout(onReady, 600);
      } else {
        toast.error(r.error ?? t('codable.glSaveFail'));
      }
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiClientError ? e.message : String(e);
      toast.error(t('common.errorWithMessage', 'Chyba: {{message}}', { message: msg }));
    },
  });

  const id = check.data?.identity;
  const gl = check.data?.gitlab;
  const url = check.data?.onboarding_url;

  if (check.data?.access_mode === 'delegation') return (
    <div className='fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4'>
      <div className='bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-lg w-full space-y-4'>
        <h3 className='font-bold text-white'>{t('codable.glDelegationTitle', 'GitLab přístup pod tvou identitou')}</h3>
        <p className='text-sm text-gray-300'>{t('codable.glDelegationIntro', 'ACM ověřuje tvoji SSO vazbu a používá tvoje GitLab oprávnění. Osobní token se nevkládá.')}</p>
        <p className={gl?.has_credentials ? 'text-green-300' : 'text-amber-300'}>{check.data.hint}</p>
        <div className='flex justify-end gap-2'>
          <Button variant='secondary' onClick={onCancel}>{t('common.cancel')}</Button>
          {!gl?.has_credentials && <Button onClick={() => void check.refetch()} loading={check.isFetching}>{t('codable.glDelegationRetry', 'Znovu ověřit přístup')}</Button>}
        </div>
      </div>
    </div>
  );

  return (
    <div className='fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4'>
      <div className='bg-gray-900 border border-gray-800 rounded-2xl p-6 max-w-lg w-full space-y-5'>
        <div>
          <h3 className='font-bold text-white text-lg mb-1'>{t('codable.glTitle')}</h3>
          <p className='text-sm text-gray-400'>
            {t(
              'codable.glDelegationIntro',
              'ACM ověřuje tvoji SSO vazbu a používá tvoje GitLab oprávnění. Osobní token se nevkládá.',
            )}
          </p>
        </div>

        {check.isLoading && (
          <div className='flex items-center gap-2 text-gray-500'>
            <Loader2 size={16} className='animate-spin' /> {t('common.loading')}
          </div>
        )}

        {check.data && !gl?.host && (
          <div className='bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm text-red-300'>
            {check.data.hint ?? t('codable.glNoPlatform')}
          </div>
        )}

        {gl?.host && (
          <>
            {/* Krok 1 — identity check */}
            <StepRow
              icon={<User size={16} />}
              active={step === 'check'}
              done={step !== 'check'}
              title={t('codable.glLogin')}
              detail={
                id?.entity_id
                  ? `${id.entity_id}${id.tenant_id ? ` (${id.tenant_id})` : ''}`
                  : id?.authenticated
                  ? t('codable.glAuthed')
                  : t('codable.glLogin')
              }
            />

            {/* Krok 2 — open GitLab */}
            <StepRow
              icon={<ExternalLink size={16} />}
              active={step === 'open'}
              done={step === 'paste' || step === 'done'}
              title={t('codable.glCreatePat')}
              detail={t(
                'codable.glSsoDetail',
                '{{host}} — otevře se v novém tabu, KC SSO tě přihlásí',
                { host: new URL(gl.host).host },
              )}
            >
              {step === 'open' && url && (
                <div className='mt-2 space-y-2'>
                  <a
                    href={url}
                    target='_blank'
                    rel='noreferrer'
                    onClick={() => setStep('paste')}
                    className='inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm'
                  >
                    <ExternalLink size={14} />
                    {t('codable.glOpen')}
                  </a>
                  <p className='text-xs text-gray-500'>
                    {t(
                      'codable.glPatHintBefore',
                      'V GitLabu klikni',
                    )}{' '}
                    <span className='font-mono'>Create personal access token</span>{' '}
                    {t(
                      'codable.glPatHintAfter',
                      '(name i scopes už jsou předvyplněné), zkopíruj token — začíná',
                    )}{' '}
                    <span className='font-mono'>glpat-</span>{' '}
                    {t('codable.glPatHintEnd', '— a vlož ho tady.')}
                  </p>
                </div>
              )}
            </StepRow>

            {/* Krok 3 — paste */}
            <StepRow
              icon={<KeyRound size={16} />}
              active={step === 'paste'}
              done={step === 'done'}
              title={t('codable.glInsertToken')}
            >
              {step === 'paste' && (
                <div className='mt-2 space-y-2'>
                  <input
                    className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-2 text-sm text-gray-200 focus:border-blue-500 focus:outline-none font-mono'
                    placeholder='glpat-…'
                    value={pat}
                    onChange={(e) => setPat(e.target.value.trim())}
                    disabled={save.isPending}
                    autoFocus
                  />
                  <div className='flex justify-end gap-2'>
                    <Button variant='secondary' onClick={() => setStep('open')} disabled={save.isPending}>
                      {t('common.back')}
                    </Button>
                    <Button
                      variant='primary'
                      onClick={() => save.mutate()}
                      disabled={!pat.trim() || save.isPending}
                      loading={save.isPending}
                    >
                      {t('common.save')}
                    </Button>
                  </div>
                </div>
              )}
            </StepRow>

            {step === 'done' && (
              <div className='flex items-center gap-2 text-emerald-400 text-sm'>
                <CheckCircle2 size={18} /> {t('codable.done')}
              </div>
            )}
          </>
        )}

        <div className='flex justify-end pt-2 border-t border-gray-800'>
          <Button variant='ghost' onClick={onCancel}>{t('codable.close')}</Button>
        </div>
      </div>
    </div>
  );
}

function StepRow({
  icon,
  title,
  detail,
  active,
  done,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  detail?: string;
  active: boolean;
  done: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`border rounded-lg p-3 ${
        active
          ? 'border-blue-500/50 bg-blue-500/5'
          : done
          ? 'border-emerald-500/30 bg-emerald-500/5'
          : 'border-gray-800 bg-gray-950'
      }`}
    >
      <div className='flex items-start gap-3'>
        <div
          className={`mt-0.5 ${
            done ? 'text-emerald-400' : active ? 'text-blue-400' : 'text-gray-600'
          }`}
        >
          {done ? <CheckCircle2 size={16} /> : icon}
        </div>
        <div className='flex-1 min-w-0'>
          <div className='text-sm font-medium text-gray-200'>{title}</div>
          {detail && <div className='text-xs text-gray-500 mt-0.5 truncate'>{detail}</div>}
          {children}
        </div>
      </div>
    </div>
  );
}
